import {
  $ppidAtom,
  emptyPpId,
  $pageViewIdAtom,
  getEmptyOrPersistedPPID
} from '@client/core/atoms/config.js';
import localStorage from '@client/core/utils/localStorage.js';
import pulse from '@finn-no/pulse-sdk';

import { debug } from './.debug.js';

export type PulseTracker = {
  getAdvertisingIdentifiers(): Promise<PPIDResponse>;
};

export type PPIDObject = {
  ppId1?: string;
  ppId2?: string;
  adId?: string;
};

export type PPIDResponse = {
  delta?: PPIDObject;
  adform?: PPIDObject;
  xandr?: PPIDObject;
};

export type TrackerData = {
  pageViewId?: string;
};

async function getTracker(): Promise<PulseTracker> {
  try {
    const { promise, resolve, reject } = Promise.withResolvers<PulseTracker>();

    try {
      const timeout = setTimeout(() => {
        reject(new Error('Timeout from pulse.getTracker()'));
      }, 2000);

      pulse.getTracker((tracker: PulseTracker | undefined) => {
        clearTimeout(timeout);

        if (tracker) {
          resolve(tracker);
        } else {
          reject(new Error('No tracker found from pulse.getTracker()'));
        }
      });
    } catch (error) {
      reject(error);
    }

    return await promise;
  } catch (error) {
    debug.message('Failed getting tracker!', error).error();
    throw error;
  }
}

async function getPageState(): Promise<TrackerData> {
  try {
    const { promise, resolve, reject } = Promise.withResolvers<TrackerData>();

    try {
      const timeout = setTimeout(() => {
        reject(new Error('Timeout from pulse.getPageState()'));
      }, 200);

      pulse.getPageState((pageState: TrackerData | undefined) => {
        clearTimeout(timeout);

        if (pageState) {
          resolve(pageState);
        } else {
          reject(new Error('No page state received from pulse.getPageState()'));
        }
      });
    } catch (error) {
      reject(error);
    }

    return await promise;
  } catch (error) {
    debug.message('Failed getting page state!', error).error();
    throw error;
  }
}

export const PPID_STORAGE_NAME = 'advertising-podlet.ppid';

export async function persistPPID(): Promise<PPIDResponse> {
  try {
    const tracker = await getTracker();
    debug.message('Tracker received', tracker).verbose();

    const res = await getAdvertisingIdentifiers(tracker);
    debug.message('PPID response', res).verbose();

    const persisted = getEmptyOrPersistedPPID();
    debug.message('PPID persisted', persisted).verbose();

    const ppidData = {
      ...emptyPpId,
      ...persisted,
      ...res
    } as PPIDResponse;

    if (ppidData.delta) {
      debug.message`PPID delta resolved for keys ${Object.keys(ppidData.delta)}`(
        { ppidData }
      ).info((log) => log());

      $ppidAtom.set(ppidData);
    }

    localStorage.setItem(PPID_STORAGE_NAME, ppidData);

    return ppidData;
    // Set the object on window for use in banners.tsx and other potential places
  } catch (error) {
    debug.message('Failed resolving PPID delta!', error).error();
    return $ppidAtom.get();
  }
}

export async function setPulsePageViewId(): Promise<string | null> {
  try {
    // Try to extract a pageViewId from pageState if it exists
    const pageViewId = await waitForPulsePageViewId();
    debug.message`pageViewId response: ${pageViewId}`().verbose();

    if (pageViewId) {
      $pageViewIdAtom.set(pageViewId);
    }

    return pageViewId ?? $pageViewIdAtom.get();
  } catch (error) {
    debug.message('Failed resolving pageViewId!', error).error();
    return $pageViewIdAtom.get();
  }
}

export async function getAdvertisingIdentifiers(tracker: PulseTracker) {
  // gets delta and xandr automatically
  return await tracker.getAdvertisingIdentifiers();
}

async function waitForPulsePageViewId({
  maxAttempts = 3,
  interval = 100
} = {}): Promise<string | null> {
  const startTime = Date.now();
  let i = 0;

  for (; i < maxAttempts; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    const pageState = await getPageState().catch(() => null);
    const pageViewId = pageState?.pageViewId;

    if (pageViewId) {
      const elapsedTime = Date.now() - startTime;
      debug.message`pageViewId resolved after ${i + 1} attempt(s) (${elapsedTime} ms total)`().info();

      return pageViewId;
    }

    const { promise, resolve } = Promise.withResolvers<void>();

    setTimeout(resolve, interval);
    await promise; // eslint-disable-line no-await-in-loop
  }

  const elapsedTime = Date.now() - startTime;

  debug.message`pageViewId unresolved after ${i + 1} attempt(s) (${elapsedTime} ms total)`().warn();

  return null;
}
